import { gql, useQuery } from '@apollo/client';
import { Box, Divider } from '@spaceship-fspl/components';
import { superPortfolioInformationScalars } from '@spaceship-fspl/graphql';
import { WebSuperHomepage } from '@spaceship-fspl/graphql/src/__generated__/WebSuperHomepage';
import { SuperProductStarsIcon } from '@spaceship-fspl/icons-web';
import { color, match, text } from '@spaceship-fspl/styles';
import {
  AssetAllocation,
  getHeroPageLayoutStaticProps,
  getMetadataPropsFromPrismicSlice,
  HeaderProps,
  ImportantDocumentsPanel,
  mapNodeToHeaderProps,
  Metadata,
  MetadataProps,
  PortfolioOptionsPanel,
  PortfolioPerformancePanel,
  SignUpPanel,
  StickyHeader,
  SUPER_BASIC_DOCUMENTS,
  SuperFeesPanel,
  withHeroPageLayout,
} from 'components';
import {
  getHeader,
  getSuperHomepage,
  mapSlicesToProps,
  RichText,
} from 'data/prismic';
import {
  Super_HomepageBodyBenefits,
  Super_HomepageBodyBenefitsFields,
  Super_HomepageBodyFees,
  Super_HomepageBodyFeesFields,
  Super_HomepageBodyFeesPrimary,
  Super_HomepageBodyGlobal_Index_Asset_Allocation,
  Super_HomepageBodyGlobal_Index_Asset_AllocationFields,
  Super_HomepageBodyGlobal_Index_Asset_AllocationPrimary,
  Super_HomepageBodyGrowthx_Asset_Allocation,
  Super_HomepageBodyGrowthx_Asset_AllocationFields,
  Super_HomepageBodyGrowthx_Asset_AllocationPrimary,
  Super_HomepageBodyHero,
  Super_HomepageBodyHeroPrimary,
  Super_HomepageBodyImportant_Documents,
  Super_HomepageBodyImportant_DocumentsPrimary,
  Super_HomepageBodyJoin1,
  Super_HomepageBodyJoin1Fields,
  Super_HomepageBodyJoin1Primary,
  Super_HomepageBodyPerformance,
  Super_HomepageBodyPerformanceFields,
  Super_HomepageBodyPerformancePrimary,
  Super_HomepageBodyPortfolios,
  Super_HomepageBodyPortfoliosFields,
  Super_HomepageBodyPortfoliosPrimary,
  Super_HomepageBodyRating,
  Super_HomepageBodyRatingPrimary,
  Super_HomepageBodySupersignup,
  Super_HomepageBodySupersignupPrimary,
} from 'data/prismic/types';
import { SuperPrismicPortfolio } from 'helpers/products';
import * as React from 'react';
import styled from 'styled-components';

import SuperHeroImageDesktop from './img-super-homepage.jpeg';
import { JoinPanel } from './join-panel';
import { RatingPanel } from './rating-panel.tsx';
import { ReasonsPanel } from './reasons-panel';
import { SuperSignUpPanel } from './sign-up-panel';
import { SuperHeroPanel } from './super-hero-panel';

interface SuperHomePageProps {
  headerProps?: HeaderProps;
  data: {
    Super_homepageBodyMetadata?: MetadataProps | null;
    Super_homepageBodyHero?: Super_HomepageBodyHeroPrimary | null;
    Super_homepageBodyJoin1?:
      | null
      | (Super_HomepageBodyJoin1Primary & {
          steps?: Super_HomepageBodyJoin1Fields[] | null;
        });
    Super_homepageBodyRating?: Super_HomepageBodyRatingPrimary | null;
    Super_homepageBodyPortfolios?:
      | null
      | (Super_HomepageBodyPortfoliosPrimary & {
          portfolios?: Super_HomepageBodyPortfoliosFields[] | null;
        });
    Super_homepageBodyGrowthx_asset_allocation?:
      | null
      | (Super_HomepageBodyGrowthx_Asset_AllocationPrimary & {
          allocations?:
            | Super_HomepageBodyGrowthx_Asset_AllocationFields[]
            | null;
        });
    Super_homepageBodyGlobal_index_asset_allocation?:
      | null
      | (Super_HomepageBodyGlobal_Index_Asset_AllocationPrimary & {
          allocations?:
            | Super_HomepageBodyGlobal_Index_Asset_AllocationFields[]
            | null;
        });
    Super_homepageBodyFees?:
      | null
      | (Super_HomepageBodyFeesPrimary & {
          fees?: Super_HomepageBodyFeesFields[] | null;
        });
    Super_homepageBodyPerformance?:
      | null
      | (Super_HomepageBodyPerformancePrimary & {
          portfolios?: Super_HomepageBodyPerformanceFields[] | null;
        });
    Super_homepageBodyBenefits?: Super_HomepageBodyBenefitsFields[] | null;
    Super_homepageBodyImportant_documents?: Super_HomepageBodyImportant_DocumentsPrimary | null;
    Super_homepageBodySupersignup?: Super_HomepageBodySupersignupPrimary | null;
  };
}

const RichTextWrapper = styled.div`
  p {
    ${text({ variant: 1 })}
    ${color('neutral.070')}
    margin: 0;
    text-align: center;

    ${match('md')`
      text-align: left;
    `}
  }
`;

const SuperHomePage: React.FC<React.PropsWithChildren<SuperHomePageProps>> = ({
  headerProps,
  data: {
    Super_homepageBodyMetadata: metadata,
    Super_homepageBodyHero: superHero,
    Super_homepageBodyFees: fees,
    Super_homepageBodyJoin1: simpleToJoin,
    Super_homepageBodyRating: rating,
    Super_homepageBodyPerformance: performance,
    Super_homepageBodyImportant_documents: documents,
    Super_homepageBodyPortfolios: investmentOptions,
    Super_homepageBodyGrowthx_asset_allocation: growthxAssetsAllocation,
    Super_homepageBodyGlobal_index_asset_allocation: globalIndexAssetAllocation,
    Super_homepageBodyBenefits: benefits,
    Super_homepageBodySupersignup: superSignup,
  },
}) => {
  const [selectedPortfolio, setSelectedPortfolio] =
    React.useState<SuperPrismicPortfolio>('growthx');

  const resp = useQuery<WebSuperHomepage>(gql`
    query WebSuperHomepage {
      global_index: superPortfolioInformation(portfolio: GLOBAL_INDEX) {
        id
        ...superPortfolioInformationFields
      }
      growthx: superPortfolioInformation(portfolio: GROWTHX) {
        id
        ...superPortfolioInformationFields
      }
    }
    ${superPortfolioInformationScalars}
  `);

  return (
    <>
      {metadata && <Metadata {...metadata} />}
      {superHero && (
        <>
          <SuperHeroPanel
            product={superHero?.product || ''}
            title={superHero?.title || ''}
            tagline={
              <RichTextWrapper>
                <RichText render={superHero?.richtagline} />
              </RichTextWrapper>
            }
            backgroundImageURL={SuperHeroImageDesktop}
            mobileIcon={
              <SuperProductStarsIcon color="neutral.000" size="xxxl" />
            }
            headerComponent={(props) => (
              <StickyHeader {...headerProps} {...props} />
            )}
          />
          {performance && (
            <PortfolioPerformancePanel
              backgroundColor="neutral.015"
              title={performance.title ?? ''}
              disclaimer={<RichText render={performance.richdisclaimer} />}
              portfolios={performance.portfolios?.map(({ type }) => {
                const castedType = type
                  ? (type as SuperPrismicPortfolio)
                  : 'growthx';
                const portfolio = resp.data?.[castedType];

                return {
                  type: castedType,
                  name: portfolio?.name ?? '',
                  title: portfolio?.title ?? '',
                  inceptionDate: portfolio?.inceptionDate,
                  sinceInceptionPerformancePercentage:
                    portfolio?.sinceInceptionPerformancePercentage,
                  performanceAsAt: portfolio?.performanceAsAt,
                };
              })}
              selectedPortfolio={selectedPortfolio}
              onPortfolioChange={setSelectedPortfolio}
            />
          )}
          {simpleToJoin && (
            <JoinPanel
              title={simpleToJoin?.title || ''}
              tagline={simpleToJoin?.tagline || ''}
              steps={simpleToJoin?.steps || []}
            />
          )}
          {rating && (
            <RatingPanel
              title={rating?.title || ''}
              description={rating?.description || ''}
              imageUrl={rating?.image?.url || ''}
            />
          )}
          {investmentOptions &&
            growthxAssetsAllocation &&
            globalIndexAssetAllocation && (
              <PortfolioOptionsPanel
                variant="super"
                title={investmentOptions.title || ''}
                tagline={investmentOptions.tagline || ''}
                portfolios={
                  investmentOptions.portfolios?.map(({ type, description }) => {
                    const castedType = type
                      ? (type as SuperPrismicPortfolio)
                      : 'growthx';
                    const portfolio = resp.data?.[castedType];
                    const portfolioAssetAllocation =
                      castedType === 'growthx'
                        ? growthxAssetsAllocation
                        : globalIndexAssetAllocation;

                    return {
                      type: castedType,
                      name: portfolio?.name ?? '',
                      title: portfolio?.title ?? '',
                      description: description ?? '',
                      productSlug: 'super',
                      assetAllocationsTitle:
                        portfolioAssetAllocation.title || '',
                      assetAllocations:
                        (portfolioAssetAllocation.allocations as AssetAllocation[]) ||
                        [],
                    };
                  }) || []
                }
                selectedPortfolio={selectedPortfolio}
                onPortfolioChange={setSelectedPortfolio}
              />
            )}

          {fees && (
            <SuperFeesPanel
              backgroundColor="neutral.015"
              title={fees.title || ''}
              disclaimer={<RichText render={fees.richdisclaimer} />}
              adminFeeWaiver={fees.admin_fee_waiver_copy}
              feeTableTitle="Fees and Costs"
              fees={fees.fees}
              portfolios={[
                {
                  type: 'growthx',
                  title: resp.data?.growthx?.title ?? '',
                },
                {
                  type: 'global_index',
                  title: resp.data?.global_index?.title ?? '',
                },
              ]}
            />
          )}
          {benefits && (
            <Box paddingBottom="xxl">
              <Divider color="neutral.050" />
              <ReasonsPanel
                reasons={
                  [
                    benefits?.find(({ type }) => type === 'accessible'),
                    benefits?.find(({ type }) => type === 'simple'),
                    benefits?.find(({ type }) => type === 'join'),
                    benefits?.find(({ type }) => type === 'fhss')
                      ? {
                          ...benefits?.find(({ type }) => type === 'fhss'),
                          learnMoreLink: '/super/first-home-super-saver',
                        }
                      : undefined,
                  ].filter(Boolean) as Array<Super_HomepageBodyBenefitsFields>
                }
              />
            </Box>
          )}

          {documents && (
            <>
              <Divider color="neutral.050" />
              <ImportantDocumentsPanel
                title={documents.title || ''}
                tagline={documents.summary || ''}
                documents={SUPER_BASIC_DOCUMENTS}
              />
            </>
          )}

          {superSignup ? (
            <SuperSignUpPanel
              title={superSignup.title}
              tagline={superSignup.tagline}
              disclaimer={
                superSignup.richdisclaimer && (
                  <RichText render={superSignup.richdisclaimer} />
                )
              }
            />
          ) : (
            <SignUpPanel
              title="Invest in your future, so you can live the life you want to live"
              tagline="Get started in five minutes."
              product="super"
            />
          )}
        </>
      )}
    </>
  );
};

export const getStaticProps = getHeroPageLayoutStaticProps<SuperHomePageProps>(
  async () => {
    const header = await getHeader();
    const document = await getSuperHomepage();
    const data = mapSlicesToProps(document, {
      Super_homepageBodyMetadata: (slice) =>
        getMetadataPropsFromPrismicSlice(slice),
      Super_homepageBodyHero: (slice: Super_HomepageBodyHero) => ({
        ...slice.primary,
      }),
      Super_homepageBodyJoin1: (slice: Super_HomepageBodyJoin1) => ({
        ...slice.primary,
        steps: slice.fields,
      }),
      Super_homepageBodyRating: (slice: Super_HomepageBodyRating) => ({
        ...slice.primary,
      }),
      Super_homepageBodyPortfolios: (slice: Super_HomepageBodyPortfolios) => ({
        ...slice.primary,
        portfolios: slice.fields,
      }),
      Super_homepageBodyGrowthx_asset_allocation: (
        slice: Super_HomepageBodyGrowthx_Asset_Allocation,
      ) => ({
        ...slice.primary,
        allocations: slice.fields,
      }),
      Super_homepageBodyGlobal_index_asset_allocation: (
        slice: Super_HomepageBodyGlobal_Index_Asset_Allocation,
      ) => ({
        ...slice.primary,
        allocations: slice.fields,
      }),
      Super_homepageBodyFees: (slice: Super_HomepageBodyFees) => ({
        ...slice.primary,
        fees: slice.fields,
      }),
      Super_homepageBodyPerformance: (
        slice: Super_HomepageBodyPerformance,
      ) => ({
        ...slice.primary,
        portfolios: slice.fields,
      }),
      Super_homepageBodyBenefits: (slice: Super_HomepageBodyBenefits) =>
        slice.fields,
      Super_homepageBodyImportant_documents: (
        slice: Super_HomepageBodyImportant_Documents,
      ) => ({ ...slice.primary }),
      Super_homepageBodySupersignup: (
        slice: Super_HomepageBodySupersignup,
      ) => ({ ...slice.primary }),
    });
    return {
      props: {
        headerProps: mapNodeToHeaderProps(header),
        data,
      },
    };
  },
);

export default withHeroPageLayout({ product: 'super', showSignUpPanel: false })(
  SuperHomePage,
);
